// src/components/Toast.jsx
import { Toaster, toast as hotToast } from "react-hot-toast";
import { Check, X, Loader2, AlertTriangle, Info } from "lucide-react";

// Icon components for each toast type
const SuccessIcon = () => (
  <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
    <Check className="w-4 h-4 text-white" strokeWidth={3} />
  </div>
);

const ErrorIcon = () => (
  <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
    <X className="w-4 h-4 text-white" strokeWidth={3} />
  </div>
);

const WarningIcon = () => (
  <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
    <span className="text-white font-bold text-sm">!</span>
  </div>
);

const InfoIcon = () => (
  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
    <span className="text-white font-bold text-sm">i</span>
  </div>
);

// Toast type configurations
const toastConfig = {
  success: {
    icon: <SuccessIcon />,
    borderColor: '#16a34a',
    bgColor: '#dcfce7',
    darkBgColor: 'rgba(22, 163, 74, 0.15)',
    title: 'Success',
  },
  error: {
    icon: <ErrorIcon />,
    borderColor: '#dc2626',
    bgColor: '#fee2e2',
    darkBgColor: 'rgba(220, 38, 38, 0.15)',
    title: 'Error',
  },
  warning: {
    icon: <WarningIcon />,
    borderColor: '#f59e0b',
    bgColor: '#fef3c7',
    darkBgColor: 'rgba(245, 158, 11, 0.15)',
    title: 'Warning',
  },
  info: {
    icon: <InfoIcon />,
    borderColor: '#3b82f6',
    bgColor: '#dbeafe',
    darkBgColor: 'rgba(59, 130, 246, 0.15)',
    title: 'Informational',
  },
};

// Custom toast component with the new design
const CustomToast = ({ t, type, title, message }) => {
  const config = toastConfig[type] || toastConfig.info;
  const isDark = document.documentElement.classList.contains('dark');

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    hotToast.dismiss(t.id);
  };

  return (
    <div
      className="flex items-start gap-2 w-72 rounded-md shadow-md overflow-hidden"
      style={{
        background: isDark ? config.darkBgColor : config.bgColor,
        borderLeft: `3px solid ${config.borderColor}`,
        animation: t.visible
          ? 'slideInRight 0.3s ease-out forwards'
          : 'slideOutRight 0.2s ease-in forwards',
      }}
    >
      <div className="flex items-start gap-2 p-3 flex-1">
        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: config.borderColor }}>
          {type === 'success' && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
          {type === 'error' && <X className="w-3 h-3 text-white" strokeWidth={3} />}
          {type === 'warning' && <span className="text-white font-bold text-xs">!</span>}
          {type === 'info' && <span className="text-white font-bold text-xs">i</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-xs text-zinc-900 dark:text-zinc-100">
            {title || config.title}
          </p>
          {message && (
            <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300">
              {message}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="p-2 text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Base toast function for custom renders (like undo toasts)
const toastBase = (render, options = {}) => hotToast(render, options);

// Custom toast wrapper with the new design
export const toast = Object.assign(toastBase, {
  success: (message, options = {}) => {
    const { title, ...rest } = options;
    return hotToast.custom(
      (t) => <CustomToast t={t} type="success" title={title} message={message} />,
      { duration: 2500, ...rest } // Quick confirmation
    );
  },

  error: (message, options = {}) => {
    const { title, ...rest } = options;
    return hotToast.custom(
      (t) => <CustomToast t={t} type="error" title={title} message={message} />,
      { duration: 8000, ...rest } // Errors stay longer to read
    );
  },

  warning: (message, options = {}) => {
    const { title, ...rest } = options;
    return hotToast.custom(
      (t) => <CustomToast t={t} type="warning" title={title} message={message} />,
      { duration: 6000, ...rest } // Warnings need attention
    );
  },

  info: (message, options = {}) => {
    const { title, ...rest } = options;
    return hotToast.custom(
      (t) => <CustomToast t={t} type="info" title={title} message={message} />,
      { duration: 4000, ...rest } // Info is moderate
    );
  },

  loading: (message, options = {}) => {
    return hotToast.custom(
      (t) => (
        <div
          className="flex items-center gap-2 w-72 rounded-md shadow-md p-3 bg-zinc-100 dark:bg-zinc-800"
          style={{
            borderLeft: '3px solid #3b82f6',
            animation: t.visible
              ? 'slideInRight 0.3s ease-out forwards'
              : 'slideOutRight 0.2s ease-in forwards',
          }}
        >
          <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
          <p className="text-xs font-medium text-zinc-700 dark:text-zinc-200">{message}</p>
        </div>
      ),
      { duration: Infinity, ...options }
    );
  },

  // For updating a loading toast (e.g., loading -> success)
  promise: (promise, messages, options = {}) => {
    return hotToast.promise(promise, messages, options);
  },

  // Dismiss a specific toast or all toasts
  dismiss: (toastId) => hotToast.dismiss(toastId),

  // Custom toast (for more control)
  custom: (render, options = {}) => hotToast.custom(render, options),
});

export default function ToastProvider() {
  return (
    <Toaster
      position="bottom-right"
      gutter={12}
      containerStyle={{
        bottom: 24,
        right: 24,
      }}
      toastOptions={{
        duration: 4000,
      }}
    />
  );
}